/**
 * Platform-agnostic receipt element model.
 *
 * The receipt formatter builds an array of these elements.
 * Renderers convert them to platform-specific formats:
 *   - toDantSuMarkup() → Android printing via DantSu library
 *   - ReceiptPreview component → React JSX for visual preview
 *   - [future] toEscPosBytes() → iOS printing
 */

/** A single line of text */
export interface ReceiptText {
  type: 'text'
  align: 'left' | 'center' | 'right'
  text: string
  bold?: boolean
  /** Double height on the printer */
  tall?: boolean
}

/** Two-column row: left label + right value (e.g., "Subtotal:" / "10,00 €") */
export interface ReceiptColumns {
  type: 'columns'
  left: string
  right: string
  leftBold?: boolean
  rightBold?: boolean
  /** Double height on the printer */
  tall?: boolean
}

/** Horizontal separator line */
export interface ReceiptLine {
  type: 'line'
  char?: string // default '_'
}

/** Logo or other image (pre-converted to hex for DantSu; src only renders the preview) */
export interface ReceiptImage {
  type: 'image'
  hex: string
  src?: string
}

/** QR code */
export interface ReceiptQrCode {
  type: 'qrcode'
  text: string
  size?: number // default 25
  /** Printed by the printer firmware after the text chunks (labelSettings.useNativeQrCode), not as DantSu markup */
  native?: boolean
}

/** Boundary between the separately sent print chunks (header / items / footer, as in the old app) */
export interface ReceiptChunkBreak {
  type: 'chunkBreak'
}

/** Paper feed / empty lines */
export interface ReceiptFeed {
  type: 'feed'
  lines?: number // default 1
}

export type ReceiptElement =
  | ReceiptText
  | ReceiptColumns
  | ReceiptLine
  | ReceiptImage
  | ReceiptQrCode
  | ReceiptChunkBreak
  | ReceiptFeed

/**
 * Label/printer settings from the backend.
 * Fetched from GET /labelSettings?restaurantId=X
 */
export interface LabelSettings {
  printLogo: boolean
  printDeliveryQr: boolean
  printMenuItemDescription: boolean
  printerDpi: number
  printerWidthMm: number
  printerNbrCharactersPerLine: number
  printerCharsetName: string
  printerCharsetCommand: number
  useNativeQrCode: boolean
}

export const DEFAULT_LABEL_SETTINGS: LabelSettings = {
  printLogo: true,
  printDeliveryQr: true,
  printMenuItemDescription: true,
  printerDpi: 203,
  printerWidthMm: 48,
  printerNbrCharactersPerLine: 25,
  printerCharsetName: 'IBM858',
  printerCharsetCommand: 19,
  useNativeQrCode: true,
}

/**
 * Restaurant info needed for receipt header.
 * Sourced from GeneralSettings (useGeneralSettings hook).
 */
export interface ReceiptRestaurantInfo {
  name: string
  streetName: string
  houseNumber: string
  postcode: string
  city: string
  phoneNumber: string
  defaultTaxRate: number
  currency: string
  /** Restaurant language (de/en/es); receipt labels use it, never the tablet's UI language */
  language?: string
}

/**
 * Menu item info needed for receipt (category grouping + tax rate).
 * Sourced from menu-editor useItems hook.
 */
export interface ReceiptMenuItemInfo {
  id: number
  menuitemcategoryId: number
  categoryName: string
  menuposition: number
  taxRate: number | null
  description: string | null
}
